import { Collapse } from '@mui/material';
import React, { useEffect } from 'react';

import getTextTruncationSx from '../../utils/getTextTruncationSx';

interface WithTruncationAnimationProps {
	children: React.ReactElement;
	maxLines: number;
	renderToggleButton?: (isTruncated: boolean) => React.ReactElement;
}

const WithTruncationAnimation: React.FC<WithTruncationAnimationProps> = (props) => {
	const [isTruncated, setIsTruncated] = React.useState(true);
	const [collapsedHeight, setCollapsedHeight] = React.useState(0);
	const [isOverflowing, setIsOverflowing] = React.useState(false);
	const ref = React.useRef<HTMLElement>(null);
	const { children, maxLines, renderToggleButton } = props;

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (ref.current) {
			setIsOverflowing(ref.current.scrollHeight > ref.current.clientHeight);
			setCollapsedHeight(
				Math.min(
					Number.parseFloat(getComputedStyle(ref.current).lineHeight) * maxLines,
					ref.current.scrollHeight
				)
			);
		}
	}, [ref.current]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		Object.assign(ref.current!.style, {
			...getTextTruncationSx(isTruncated ? maxLines : 'unset'),
		});
	}, [isTruncated]);

	return (
		<>
			<Collapse in={!isTruncated} collapsedSize={collapsedHeight}>
				{React.cloneElement(React.Children.only(children), { ref })}
			</Collapse>
			{isOverflowing &&
				renderToggleButton &&
				React.cloneElement(renderToggleButton(isTruncated), {
					onClick: () => setIsTruncated((prev) => !prev),
				})}
		</>
	);
};

export default WithTruncationAnimation;
